/**
 * Playbook (Heather 2026-08-05; reorganized Ken 2026-08-12) — the attorney's
 * retrieval list of PULLS: individual passages (or whole-email marks) she
 * flagged as work product worth keeping.
 *
 * One card = one pull. A WorkProduct flag (one per email) can hold several
 * pulls; each excerpt entry becomes its own card here. The page is a centered
 * column with a clickable type-filter line and flat per-pull cards.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { workProduct, type WorkProductItem, type WorkProductType } from '../api'

const WP_KEY = ['work-product', 'mine'] as const

// Type-tag labels (mirror of the Pull modal). Short slugs are used on the
// filter line ("advice 3"); these full labels on the card tags.
const PULL_TYPE_LABELS: Record<WorkProductType, string> = {
  advice: 'Advice given',
  language: 'Language drafted',
}
const PULL_TYPE_ORDER: WorkProductType[] = ['advice', 'language']

const ORANGE = '#BA7517'
const TAG_FILL = '#F5E6D3'
const TAG_TEXT = '#854F0B'

function fmtDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—'
}

// One flattened pull (card) derived from a WorkProduct flag's excerpts.
type PullCard = {
  key: string
  itemId: number
  index: number | null   // excerpt index within the flag, or null for a
                          // legacy whole-email mark (empty excerpts)
  artifactId: number
  date: string | null
  matterCode: string | null
  matterName: string | null
  types: WorkProductType[]
  exact: string
  where: string          // markup pulls only: the document location; '' for emails
  wholeEmail: boolean
  note: string
  summary: string
  sortKey: string        // pull time (excerpt.created_at) — newest first
}

function flatten(items: WorkProductItem[]): PullCard[] {
  const out: PullCard[] = []
  for (const it of items) {
    const base = {
      itemId: it.id,
      artifactId: it.artifact,
      date: it.artifact_source_date,
      matterCode: it.matter_composite_id,
      matterName: it.matter_name,
      summary: it.issue_summary || '',
    }
    const excs = it.excerpts || []
    if (excs.length > 0) {
      excs.forEach((ex, i) => {
        const types = (ex.types || []).filter(
          (t): t is WorkProductType => t in PULL_TYPE_LABELS,
        )
        out.push({
          ...base,
          key: `${it.id}:${i}`,
          index: i,
          types,
          exact: ex.exact || '',
          where: ex.where || '',
          wholeEmail: !!ex.whole_email || !ex.exact,
          note: ex.note || '',
          sortKey: ex.created_at || it.created_at,
        })
      })
    } else {
      // Legacy whole-email mark (predates per-pull excerpts).
      out.push({
        ...base,
        key: `${it.id}:whole`,
        index: null,
        types: [],
        exact: '',
        where: '',
        wholeEmail: true,
        note: it.note || '',
        sortKey: it.created_at,
      })
    }
  }
  // Newest first (by pull time). Type filtering never changes this order.
  out.sort((a, b) => (a.sortKey < b.sortKey ? 1 : a.sortKey > b.sortKey ? -1 : 0))
  return out
}

// Inline "label  value" row (item 3/4): tiny Chrome label + 8px gap, then the
// value. Used for note + summary.
function LabeledLine({ label, valueClass, children }: {
  label: string; valueClass: string; children: ReactNode
}) {
  return (
    <div className={`mt-2 ${valueClass}`}>
      <span className="text-[11px] me-text-mute" style={{ marginRight: 8 }}>{label}</span>
      {children}
    </div>
  )
}

export default function WorkProduct() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const q = useQuery({ queryKey: WP_KEY, queryFn: () => workProduct.list(true) })
  const items: WorkProductItem[] = q.data ?? []

  const pulls = useMemo(() => flatten(items), [items])
  const counts = useMemo(() => ({
    advice: pulls.filter((p) => p.types.includes('advice')).length,
    language: pulls.filter((p) => p.types.includes('language')).length,
  }), [pulls])

  // Type filter — additive with each other, exclusive with "all" (empty set).
  const [active, setActive] = useState<Set<WorkProductType>>(new Set())
  const isAll = active.size === 0
  const toggle = (t: WorkProductType) =>
    setActive((prev) => {
      const n = new Set(prev)
      n.has(t) ? n.delete(t) : n.add(t)
      return n
    })
  const visible = isAll ? pulls : pulls.filter((p) => p.types.some((t) => active.has(t)))

  const removeMut = useMutation({
    mutationFn: (p: PullCard) =>
      p.index != null ? workProduct.removeExcerpt(p.itemId, p.index) : workProduct.unmark(p.itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['work-product'] }),
  })

  function openMatter(p: PullCard) {
    // Return to the source artifact, highlighting the passage (whole-email
    // pulls just open the email). The viewer reads ?wp= and scrolls to it.
    const wp = !p.wholeEmail && p.exact ? `?wp=${encodeURIComponent(p.exact.slice(0, 300))}` : ''
    navigate(`/artifact/${p.artifactId}${wp}`)
  }

  return (
    <div className="me-chrome me-scroll h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="mb-1 text-[18px] font-semibold me-text">Advice Given and Language Drafted</h1>
        <p className="mb-4 text-[12px] me-text-mute">
          Passages and emails you pulled as work product worth keeping. Pull one
          from any email's detail pane (the <span className="me-mono">pull</span> button).
        </p>

        {q.isLoading && (
          <div className="flex items-center gap-2 text-[12px] me-text-mute">
            <Loader2 size={13} className="animate-spin" /> Loading…
          </div>
        )}
        {q.isError && (
          <div className="text-[12px] text-[color:var(--me-urgent)]">Could not load your work product.</div>
        )}

        {!q.isLoading && !q.isError && pulls.length === 0 && (
          <div className="text-[12px] me-text-mute">
            Nothing pulled yet. Open an email and click <span className="me-mono">pull</span> to keep it here.
          </div>
        )}

        {pulls.length > 0 && (
          <>
            {/* Filter line — clickable segments; active in orange. */}
            <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
              <button
                onClick={() => setActive(new Set())}
                className={`hover:me-text ${isAll ? 'font-medium' : 'me-text-mute'}`}
                style={isAll ? { color: ORANGE } : undefined}
              >
                {pulls.length} pull{pulls.length === 1 ? '' : 's'}
              </button>
              {PULL_TYPE_ORDER.map((t) => (
                <button
                  key={t}
                  onClick={() => toggle(t)}
                  className={`hover:me-text ${active.has(t) ? '' : 'me-text-mute'}`}
                  style={active.has(t) ? { color: ORANGE } : undefined}
                >
                  {t} {counts[t]}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              {visible.map((p) => (
                <div
                  key={p.key}
                  tabIndex={0}
                  className="group focus:outline-none"
                  style={{ border: '0.5px solid var(--me-border)', borderRadius: 10, padding: '1.1rem 1.25rem' }}
                >
                  {/* 1 · Header row — tags left, date · matter-id right. */}
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-1">
                      {p.types.map((t) => (
                        <span
                          key={t}
                          className="text-[11px]"
                          style={{ background: TAG_FILL, color: TAG_TEXT, borderRadius: 4, padding: '1px 6px' }}
                        >
                          {PULL_TYPE_LABELS[t]}
                        </span>
                      ))}
                    </div>
                    <div className="shrink-0 me-mono text-[11px] me-text-mute" title={p.matterName || undefined}>
                      {fmtDate(p.date)}{p.matterCode ? ` · ${p.matterCode}` : ''}
                    </div>
                  </div>

                  {/* 2 · Passage (Record) — or, for a whole-email pull, the
                         summary in this slot (never the full body). */}
                  {p.wholeEmail ? (
                    <div className="mt-3 text-[15px] me-text" style={{ lineHeight: 1.6 }}>
                      <span className="text-[11px] me-text-mute" style={{ marginRight: 8 }}>summary</span>
                      {p.summary || <span className="me-text-mute">(no summary)</span>}
                    </div>
                  ) : (
                    <div className="me-record mt-3 text-[15px] me-text" style={{ lineHeight: 1.6 }}>
                      “{p.exact}”
                    </div>
                  )}

                  {/* 3 · Where — markup pulls only: the document location the
                         redline/comment sits at. Omitted for email pulls. */}
                  {p.where && (
                    <LabeledLine label="where" valueClass="text-[13px] me-text-mute me-mono">{p.where}</LabeledLine>
                  )}

                  {/* 4 · Notes — omitted entirely when empty. */}
                  {p.note && (
                    <LabeledLine label="notes" valueClass="text-[13px] me-text-dim">{p.note}</LabeledLine>
                  )}

                  {/* 4 · Summary — passage pulls only (whole-email shows it above). */}
                  {!p.wholeEmail && p.summary && (
                    <LabeledLine label="summary" valueClass="text-[13px] me-text-mute">{p.summary}</LabeledLine>
                  )}

                  {/* 5 · Actions — text, right-aligned, revealed on hover/focus. */}
                  <div className="mt-3 flex justify-end gap-4 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                    <button onClick={() => openMatter(p)} className="text-[12px] me-text-mute hover:me-text">
                      Open Matter
                    </button>
                    <button
                      onClick={() => removeMut.mutate(p)}
                      disabled={removeMut.isPending}
                      className="text-[12px] me-text-mute hover:me-text"
                    >
                      Remove Pull
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
